import type React from 'react'

interface MetricPillProps {
  icon?: React.ReactNode
  count?: number
  label?: string
  color?: 'blue' | 'green' | 'rose' | 'purple' | 'orange' | 'gray' | 'yellow'
  size?: 'sm' | 'md' | 'lg'
  variant?: 'default' | 'outline' | 'subtle'
  title?: string
  className?: string
}

export function MetricPill({
  icon,
  count,
  label,
  color = 'gray',
  size = 'sm',
  variant = 'default',
  title,
  className = '',
}: MetricPillProps) {
  const sizeClasses = {
    sm: 'text-[11px] px-2 py-0.5 gap-1',
    md: 'text-xs px-2.5 py-1 gap-1.5',
    lg: 'text-sm px-3 py-1.5 gap-2',
  }

  const colorClasses = {
    blue: {
      default: 'bg-blue-100 text-blue-700 border-blue-200',
      outline: 'border-blue-300 text-blue-700 hover:bg-blue-50',
      subtle: 'hover:bg-blue-50 text-gray-700 hover:text-blue-600 border-gray-200/70',
    },
    green: {
      default: 'bg-green-100 text-green-700 border-green-200',
      outline: 'border-green-300 text-green-700 hover:bg-green-50',
      subtle: 'hover:bg-green-50 text-gray-700 hover:text-green-600 border-gray-200/70',
    },
    rose: {
      default: 'bg-rose-100 text-rose-700 border-rose-200',
      outline: 'border-rose-300 text-rose-700 hover:bg-rose-50',
      subtle: 'hover:bg-rose-50 text-gray-700 hover:text-rose-600 border-gray-200/70',
    },
    purple: {
      default: 'bg-purple-100 text-purple-700 border-purple-200',
      outline: 'border-purple-300 text-purple-700 hover:bg-purple-50',
      subtle: 'hover:bg-purple-50 text-gray-700 hover:text-purple-600 border-gray-200/70',
    },
    orange: {
      default: 'bg-orange-100 text-orange-700 border-orange-200',
      outline: 'border-orange-300 text-orange-700 hover:bg-orange-50',
      subtle: 'hover:bg-orange-50 text-gray-700 hover:text-orange-600 border-gray-200/70',
    },
    yellow: {
      default: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      outline: 'border-yellow-300 text-yellow-700 hover:bg-yellow-50',
      subtle: 'hover:bg-yellow-50 text-gray-700 hover:text-yellow-600 border-gray-200/70',
    },
    gray: {
      default: 'bg-gray-100 text-gray-700 border-gray-200',
      outline: 'border-gray-300 text-gray-700 hover:bg-gray-50',
      subtle: 'hover:bg-gray-50 text-gray-600 hover:text-gray-700 border-gray-200/70',
    },
  }

  const displayValue = count !== undefined ? count.toLocaleString() : label

  return (
    <span
      title={title}
      aria-label={title || `${label || count}`}
      className={`inline-flex items-center rounded-full border transition-all duration-200 hover:scale-105 ${sizeClasses[size]} ${colorClasses[color][variant]} ${className}`}
    >
      {icon && <span className="inline-flex flex-shrink-0">{icon}</span>}
      {displayValue && (
        <span className={count !== undefined ? 'font-medium tabular-nums' : 'font-medium'}>
          {displayValue}
        </span>
      )}
    </span>
  )
}
